import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Flex } from './base-components';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/config';
import { ReactComponent as Logo } from '../logo.svg';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, isAdminSession } = useAuth();
  const [isAccessDisabled, setIsAccessDisabled] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Check for access disabled flag immediately on mount
  useEffect(() => {
    const accessDisabled = localStorage.getItem('flashtools_access_disabled');
    if (accessDisabled === 'true') {
      setIsAccessDisabled(true);
      setIsLoggingOut(true);

      // Log out and redirect
      signOut(auth).finally(() => {
        localStorage.removeItem('flashtools_access_disabled');
        window.location.href = '/login?error=access_disabled';
      });
    }
  }, []);

  // Show blocking error screen if access is disabled
  if (isAccessDisabled || isLoggingOut) {
    return (
      <Flex
        direction="col"
        justify="center"
        align="center"
        minHeight="screen"
        bg="#0a0a0a"
        textColor="white"
        gap={6}
        px={4}
      >
        <Logo style={{ width: '80px', height: '80px', opacity: 0.3 }} />
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
          <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <div className="text-center max-w-md">
          <h2 className="text-xl font-semibold text-white mb-2">Access Disabled</h2>
          <p className="text-white/60 text-sm">Your Flashtools access has been disabled. Please contact support.</p>
        </div>
        <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin" />
      </Flex>
    );
  }

  if (loading) {
    return (
      <Flex
        direction="col"
        justify="center"
        align="center"
        minHeight="screen"
        bg="#0a0a0a"
        textColor="white"
        gap={6}
      >
        <Logo style={{ width: '80px', height: '80px', animation: 'pulse 2s ease-in-out infinite' }} />
        <div className="w-8 h-8 border-2 border-white/30 border-t-[#3b82f6] rounded-full animate-spin" />
      </Flex>
    );
  }

  if (!user && !isAdminSession) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}
